import { useWorkspaceDataflowId } from "@/features/workspace/context/WorkspaceContext";
import { indexedDBService } from "@/services/storage";
import { useDataflowStore } from "@/stores/dataflow";
import { useUIStore } from "@/stores/ui";
import { useUserFilesStore } from "@/stores/user-file";
import { logger } from "@/utils/debug";
import { parseClipboard } from "@/utils/parse";
import {
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconLayoutColumns, IconLayoutRows } from "@tabler/icons-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EditableHeader } from "./EditableHeader";

export function WorkspaceModalPaste({
  opened,
  onClose,
  title = "Paste Data",
}: {
  opened: boolean;
  onClose: () => void;
  title?: string;
}) {
  const [clipboardData, setClipboardData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [columnRenameMap, setColumnRenameMap] = useState<Map<string, string>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fileName, setFileName] = useState("");
  const gridApiRef = useRef<GridApi | null>(null);
  const columnWidthsRef = useRef<Map<string, number>>(new Map());
  const hasAutoSizedRef = useRef(false);

  const dataflowId = useWorkspaceDataflowId();
  const activeDataflowId = useDataflowStore((state) => state.activeDataflowId);
  const addTransformer = useDataflowStore((state) => state.addTransformer);
  const openSidebar = useUIStore((state) => state.openSidebar);
  const refreshFiles = useUserFilesStore((state) => state.refreshFiles);

  // Read clipboard on mount
  useEffect(() => {
    if (opened) {
      readClipboard();
    } else {
      // Reset state when modal closes
      setClipboardData([]);
      setColumns([]);
      setColumnRenameMap(new Map());
      setFileName("");
      setLoading(true);
      columnWidthsRef.current.clear();
      hasAutoSizedRef.current = false;
    }
  }, [opened]);

  const readClipboard = async () => {
    try {
      setLoading(true);

      const text = await navigator.clipboard.readText();
      const parsed = await parseClipboard(text);

      setClipboardData(parsed.rows);
      setColumns(parsed.columns);

      // Set default file name based on current date/time
      const defaultName = `Pasted Data ${new Date().toLocaleString()}`;
      setFileName(defaultName);
    } catch (err) {
      logger.error("Failed to read clipboard:", err);
      // Close modal if clipboard reading fails
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleHeaderValueChanged = (originalName: string, newValue: string) => {
    setColumnRenameMap((prev) => {
      const newMap = new Map(prev);
      if (newValue.trim() === "" || newValue === originalName) {
        newMap.delete(originalName);
      } else {
        newMap.set(originalName, newValue.trim());
      }
      return newMap;
    });
  };

  // Build column definitions with editable headers
  // Note: This memo only depends on columns and columnRenameMap, not fileName
  // so it won't recalculate when typing in the name field
  const colDefs = useMemo<ColDef[]>(() => {
    return columns.map((col) => {
      const displayName = columnRenameMap.get(col) || col;
      // Use preserved width if available, otherwise use default
      const preservedWidth = columnWidthsRef.current.get(col);
      return {
        field: col,
        headerName: displayName,
        sortable: true,
        filter: true,
        resizable: true,
        // Only set width if we have a preserved width, otherwise let AG Grid use default
        ...(preservedWidth ? { width: preservedWidth } : {}),
        headerComponent: (params: any) => (
          <EditableHeader
            {...params}
            displayName={displayName}
            onHeaderValueChanged={(newValue: string) =>
              handleHeaderValueChanged(col, newValue)
            }
          />
        ),
        valueFormatter: (params: any) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return "—";
          }
          return params.value;
        },
        cellStyle: (params: any) => {
          if (
            params.value === null ||
            params.value === undefined ||
            params.value === ""
          ) {
            return { color: "var(--mantine-color-gray-5)" };
          }
          return null;
        },
      };
    });
  }, [columns, columnRenameMap]);

  const handleAddToFlow = async () => {
    if (!activeDataflowId) {
      // Close modal if no active dataflow
      onClose();
      return;
    }

    try {
      setSaving(true);

      // Apply column renames to data
      const renamedData = clipboardData.map((row) => {
        const newRow: any = {};
        columns.forEach((originalCol) => {
          const newColName = columnRenameMap.get(originalCol) || originalCol;
          newRow[newColName] = row[originalCol];
        });
        return newRow;
      });

      // Get final column names after renaming
      const finalColumns = columns.map(
        (col) => columnRenameMap.get(col) || col
      );

      // Generate unique file ID
      const fileId = `paste_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      // Use the name from the input field, or fallback to default
      const finalFileName =
        fileName.trim() || `Pasted Data ${new Date().toLocaleDateString()}`;
      const fileType = "text/csv";

      // Store data in IndexedDB with dataset-compatible metadata
      const dbTableId = `user_file_${fileId}`;
      await indexedDBService.storeFileData(fileId, renamedData, {
        fileId,
        fileName: finalFileName,
        fileType,
        fileSize: JSON.stringify(renamedData).length,
        rowCount: renamedData.length,
        columnCount: finalColumns.length,
        columns: finalColumns,
        createdAt: new Date().toISOString(),
        // Dataset-compatible fields
        db_table_id: dbTableId,
        table_name: finalFileName,
        table_name_english: finalFileName,
        description_original: `Pasted data: ${finalFileName}`,
        description_english: `Pasted data: ${finalFileName}`,
      });

      // Refresh files list (this will reload from IndexedDB)
      await refreshFiles();

      // Add transformer to dataflow
      addTransformer(activeDataflowId, {
        type: "user_file",
        file_id: fileId,
        file_name: finalFileName,
        file_type: fileType,
      });

      // Close modal and open sidebar
      onClose();
      openSidebar(dataflowId, "flow");
    } catch (err) {
      logger.error("Failed to add data to flow:", err);
      // Close modal on error
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isValid = clipboardData.length >= 2 && columns.length >= 1;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="90vw"
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
      }}
    >
      <Stack gap="md" style={{ height: "100%" }}>
        {loading ? (
          <Stack
            align="center"
            gap="md"
            style={{ flex: 1, justifyContent: "center" }}
          >
            <Loader size="md" />
            <Text size="sm" c="gray.9">
              Reading clipboard...
            </Text>
          </Stack>
        ) : (
          <>
            <TextInput
              placeholder="Enter a name for this dataset"
              value={fileName}
              onChange={(e) => setFileName(e.currentTarget.value)}
              label="Dataset Name"
              required
            />

            {/* Data Table */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                width: "100%",
                overflow: "auto",
              }}
            >
              <AgGridReact
                rowData={clipboardData}
                columnDefs={colDefs}
                defaultColDef={{
                  resizable: true,
                }}
                suppressColumnVirtualisation={true}
                onGridReady={(params) => {
                  gridApiRef.current = params.api;
                }}
                onFirstDataRendered={(params) => {
                  // Auto-size columns when data is first rendered
                  if (!hasAutoSizedRef.current) {
                    setTimeout(() => {
                      params.api.autoSizeAllColumns();
                      // Save the auto-sized widths using actual column instances
                      const gridColumns = params.api.getColumns();
                      if (gridColumns) {
                        gridColumns.forEach((column) => {
                          const field = column.getColId();
                          const width = column.getActualWidth();
                          if (field && width) {
                            columnWidthsRef.current.set(field, width);
                          }
                        });
                      }
                      hasAutoSizedRef.current = true;
                    }, 100);
                  }
                }}
                onColumnResized={(params) => {
                  // Save column widths whenever they change (auto-size or manual resize)
                  if (params.column) {
                    const field = params.column.getColId();
                    const width = params.column.getActualWidth();
                    if (field && width) {
                      columnWidthsRef.current.set(field, width);
                    }
                  }
                }}
                domLayout="normal"
                suppressHorizontalScroll={false}
              />
            </div>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap="xl">
                <Flex align="center" gap={8}>
                  <IconLayoutColumns
                    color="var(--mantine-color-gray-9)"
                    size={18}
                  />
                  <Text size="sm" c="gray.9" fw={500}>
                    {columns.length} columns.
                  </Text>
                </Flex>
                <Flex align="center" gap={8}>
                  <IconLayoutRows
                    color="var(--mantine-color-gray-9)"
                    size={18}
                  />
                  <Text size="sm" c="gray.9" fw={500}>
                    {clipboardData.length} rows.
                  </Text>
                </Flex>
              </Flex>
              <Group justify="flex-end">
                <Button onClick={onClose} variant="subtle">
                  Cancel
                </Button>
                <Button
                  onClick={handleAddToFlow}
                  disabled={!isValid || saving}
                  loading={saving}
                >
                  Add to Flow
                </Button>
              </Group>
            </Flex>
          </>
        )}
      </Stack>
    </Modal>
  );
}
